<template>
    <v-container fluid class="mt-1 checklist-page">
        <v-skeleton-loader v-if="loading" type="list-item-three-line@4"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center flex-wrap mb-3" style="gap:12px;">
                <h3 class="mb-0">Booking Checklist</h3>
                <span class="progress-text">
                    {{ progress.done }} of {{ progress.total }} done<span v-if="progress.ignored">, {{ progress.ignored }}
                        ignored</span>
                </span>
                <v-spacer></v-spacer>
                <v-btn color="#3988AA" dark class="custom-buttons" @click="openTaskDialog(null)">
                    <v-icon small class="mr-1">mdi-plus</v-icon>Add Task
                </v-btn>
            </div>
            <v-progress-linear :value="progress.percent" color="#2E7D32" background-color="#E0E0E0" height="6" rounded
                class="mb-4"></v-progress-linear>

            <!-- Supplier bookings: from the Suppliers tab's booking statuses (my-stagit's per-service
                 "Confirmation for ..." tasks), not ticked here -->
            <v-card outlined class="checklist-card mb-4" v-if="supplier_summary.total > 0">
                <div class="checklist-row">
                    <v-icon :color="supplier_summary.confirmed === supplier_summary.total ? '#2E7D32' : '#BDBDBD'">
                        {{ supplier_summary.confirmed === supplier_summary.total ? 'mdi-check-circle' : 'mdi-progress-clock' }}
                    </v-icon>
                    <div class="checklist-main">
                        <div class="checklist-name">
                            Supplier bookings confirmed: {{ supplier_summary.confirmed }} of {{ supplier_summary.total }}
                            <v-chip x-small label class="ml-2" color="#F0F0F0">Automatic</v-chip>
                        </div>
                        <div v-if="supplier_summary.waiting.length" class="checklist-desc">
                            Still to confirm:
                            <span v-for="(build, i) in supplier_summary.waiting" :key="build.id">
                                {{ build.supplier ? build.supplier.supplier_name : 'Supplier' }}<span
                                    v-if="build.option"> – {{ build.option.name }}</span>
                                ({{ statusName(build.booking_status_id) }})<span
                                    v-if="i < supplier_summary.waiting.length - 1">, </span>
                            </span>
                        </div>
                    </div>
                    <v-btn text small color="#3988AA" @click="$emit('open-tab', 'tab-suppliers')">Suppliers tab</v-btn>
                </div>
            </v-card>

            <v-card outlined class="checklist-card">
                <div v-if="items.length === 0" class="pa-6 text-center grey--text">
                    No checklist for this company yet. Add the company's list in Settings → Booking Checklist, or add
                    a task for this event.
                </div>
                <div v-for="item in items" :key="item.key" class="checklist-row"
                    :class="{ 'checklist-row--closed': item.status !== 'todo' }">
                    <v-btn icon small :disabled="saving === item.key" @click="toggleDone(item)"
                        :title="item.status === 'done' ? 'Mark as to do' : 'Mark as done'">
                        <v-icon :color="statusMeta(item.status).color">{{ statusMeta(item.status).icon }}</v-icon>
                    </v-btn>
                    <div class="checklist-main">
                        <div class="checklist-name">
                            <span :class="{ 'text-decoration-line-through': item.status !== 'todo' }">{{ item.name }}</span>
                            <v-chip v-if="item.is_custom" x-small label class="ml-2" color="#E3F2FD">This event</v-chip>
                            <v-chip v-if="item.removed" x-small label class="ml-2" color="#FFEBEE"
                                title="Since removed from Settings → Booking Checklist">Removed</v-chip>
                        </div>
                        <div v-if="item.description" class="checklist-desc">{{ item.description }}</div>
                        <div class="checklist-meta">
                            <span v-if="item.status !== 'todo'">
                                {{ item.status === 'done' ? 'Done' : 'Ignored' }}<span v-if="item.status_by"> by {{
                                    item.status_by.name }}</span><span v-if="item.status_at"> · {{
                                    $date(item.status_at).format('D MMM YYYY, HH:mm') }}</span>
                            </span>
                            <span v-if="item.assignee || item.due_date" :class="{ 'overdue': isOverdue(item) }">
                                <v-icon x-small :color="isOverdue(item) ? '#C62828' : '#7C7C7C'">mdi-account-clock-outline</v-icon>
                                <span v-if="item.assignee">{{ item.assignee.name }}</span>
                                <span v-if="item.due_date"> · due {{ $date(item.due_date).format('D MMM YYYY') }}</span>
                            </span>
                        </div>
                    </div>
                    <div class="checklist-actions">
                        <v-btn text x-small :disabled="saving === item.key"
                            @click="updateItem(item, { status: item.status === 'ignored' ? 'todo' : 'ignored' })">
                            {{ item.status === 'ignored' ? 'Undo ignore' : 'Ignore' }}
                        </v-btn>
                        <v-btn icon small title="Assign / due date" @click="openTaskDialog(item)">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn v-if="item.is_custom" icon small title="Delete task" @click="deleteItem(item)">
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                    </div>
                </div>
            </v-card>
        </template>

        <v-dialog v-model="task_dialog" max-width="520" persistent>
            <v-card>
                <v-card-title>{{ editing ? (editing.is_custom ? 'Edit Task' : editing.name) : 'Add Task' }}</v-card-title>
                <v-card-text>
                    <template v-if="!editing || editing.is_custom">
                        <v-text-field v-model="form.name" label="Task" outlined dense></v-text-field>
                        <v-textarea v-model="form.description" label="Notes" outlined dense rows="2" auto-grow></v-textarea>
                    </template>
                    <v-autocomplete v-model="form.assigned_to" :items="assignees" item-text="name" item-value="id"
                        label="Assign to" outlined dense clearable
                        hint="They're emailed when assigned and again on the due date if it's still to do"
                        persistent-hint class="mb-3"></v-autocomplete>
                    <v-dialog v-model="date_menu" max-width="290px" min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="form.due_date ? $date(form.due_date).format('D MMM YYYY') : ''"
                                label="Due date" readonly outlined dense clearable v-bind="attrs" v-on="on"
                                @click:clear="form.due_date = null"></v-text-field>
                        </template>
                        <v-date-picker v-if="date_menu" v-model="form.due_date" full-width
                            @change="date_menu = false"></v-date-picker>
                    </v-dialog>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="task_dialog = false">Cancel</v-btn>
                    <v-btn dark color="#3988AA" :loading="saving === 'dialog'" @click="saveTask()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import { dedupeLinkedBuilds } from "@/utils/linkedBuilds"
import { lookups, loadLookups } from "@/common/companyLookups"

const BOOKING_STATUSES = { 1: 'Unconfirmed', 2: 'Requested', 3: 'Confirmed', 4: 'Cancelled', 5: 'Cancellation Pending' }

export default {
    data: () => ({
        loading: true,
        items: [],
        saving: null,
        task_dialog: false,
        date_menu: false,
        editing: null,
        form: { name: '', description: '', assigned_to: null, due_date: null },
    }),
    async mounted() {
        loadLookups('advisors')
        await this.getChecklist()
    },
    computed: {
        getAdvisors() {
            return lookups.advisors
        },
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
            event: 'eventsStore/getSelectedEventGetter',
        }),
        progress() {
            const total = this.items.length
            const done = this.items.filter(i => i.status === 'done').length
            const ignored = this.items.filter(i => i.status === 'ignored').length
            const open = total - ignored
            return { total, done, ignored, percent: open > 0 ? Math.round(done / open * 100) : 0 }
        },
        // One row per supplier booking (variants folded), cancelled ones left out
        supplier_summary() {
            const builds = dedupeLinkedBuilds((this.event.quotes || []).flatMap(q => q.builds || []))
                .filter(b => b.booking_status_id !== 4)
            const waiting = builds.filter(b => b.booking_status_id !== 3)
            return { total: builds.length, confirmed: builds.length - waiting.length, waiting }
        },
        assignees() {
            const users = this.getAdvisors || []
            const withAccess = users.filter(u => (u.access || []).some(a => a.company_id === this.get_selected_company.id))
            return (withAccess.length ? withAccess : users).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        },
    },
    methods: {
        async getChecklist() {
            try {
                const result = (await this.$axios.get(`admin/checklists/get_event_checklist/${this.event.id}`)).data
                if (result.response == true) {
                    this.items = result.data
                    this.$emit('progress', this.progress)
                }
            } finally {
                this.loading = false
            }
        },
        statusMeta(status) {
            return {
                done: { icon: 'mdi-check-circle', color: '#2E7D32' },
                ignored: { icon: 'mdi-minus-circle-outline', color: '#9E9E9E' },
            }[status] || { icon: 'mdi-checkbox-blank-circle-outline', color: '#7C7C7C' }
        },
        statusName(id) {
            return BOOKING_STATUSES[id] || 'Unconfirmed'
        },
        isOverdue(item) {
            return item.status === 'todo' && item.due_date && this.$date(item.due_date).isBefore(this.$date(), 'day')
        },
        toggleDone(item) {
            this.updateItem(item, { status: item.status === 'done' ? 'todo' : 'done' })
        },
        lineRef(item) {
            return item.id ? { id: item.id } : { checklist_item_id: item.checklist_item_id }
        },
        replaceItem(updated) {
            const index = this.items.findIndex(i => i.key === updated.key)
            if (index > -1) this.items.splice(index, 1, updated)
            else this.items.push(updated)
            this.$emit('progress', this.progress)
        },
        async updateItem(item, changes, key = item.key) {
            this.saving = key
            try {
                const result = (await this.$axios.post(`admin/checklists/update_event_item/${this.event.id}`,
                    { ...this.lineRef(item), ...changes })).data
                if (result.response == true) {
                    this.replaceItem(result.data)
                    return true
                }
                this.$toast.error(result.message || 'Failed to update the checklist')
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to update the checklist')
            } finally {
                this.saving = null
            }
            return false
        },
        openTaskDialog(item) {
            this.editing = item
            this.form = {
                name: item ? item.name : '',
                description: item ? (item.description || '') : '',
                assigned_to: item ? item.assigned_to : null,
                due_date: item ? item.due_date : null,
            }
            this.task_dialog = true
        },
        async saveTask() {
            if ((!this.editing || this.editing.is_custom) && !this.form.name.trim()) {
                this.$toast.error('Enter the task')
                return
            }
            const payload = { assigned_to: this.form.assigned_to || null, due_date: this.form.due_date || null }
            if (!this.editing || this.editing.is_custom) {
                payload.name = this.form.name
                payload.description = this.form.description
            }
            if (this.editing) {
                if (await this.updateItem(this.editing, payload, 'dialog')) this.task_dialog = false
                return
            }
            this.saving = 'dialog'
            try {
                const result = (await this.$axios.post(`admin/checklists/add_event_item/${this.event.id}`, payload)).data
                if (result.response == true) {
                    this.replaceItem(result.data)
                    this.task_dialog = false
                } else {
                    this.$toast.error(result.message || 'Failed to add the task')
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to add the task')
            } finally {
                this.saving = null
            }
        },
        async deleteItem(item) {
            if (!confirm(`Delete "${item.name}"?`)) return
            try {
                const result = (await this.$axios.delete(`admin/checklists/delete_event_item/${item.id}`)).data
                if (result.response == true) {
                    this.items = this.items.filter(i => i.key !== item.key)
                    this.$emit('progress', this.progress)
                } else {
                    this.$toast.error(result.message || 'Failed to delete the task')
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to delete the task')
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.checklist-page {
    font-family: Manrope;
}

.progress-text {
    color: #7C7C7C;
    font-size: 13px;
    font-weight: 600;
}

.checklist-card {
    border-radius: 12px !important;
}

.checklist-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid #F0F0F0;

    &:last-child {
        border-bottom: none;
    }
}

.checklist-row--closed .checklist-name {
    color: #9E9E9E;
}

.checklist-main {
    flex: 1;
    min-width: 0;
    padding-top: 4px;
}

.checklist-name {
    font-size: 14px;
    font-weight: 600;
}

.checklist-desc {
    font-size: 12px;
    color: #7C7C7C;
    margin-top: 2px;
    white-space: pre-line;
}

.checklist-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 12px;
    color: #7C7C7C;
    margin-top: 4px;

    .overdue {
        color: #C62828;
        font-weight: 600;
    }
}

.checklist-actions {
    display: flex;
    align-items: center;
    white-space: nowrap;
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-weight: 600;
}
</style>
